import html2pdf from 'html2pdf.js';
import { toast } from 'sonner';

/**
 * Export a note to PDF
 */
export async function exportNoteToPDF(note, options = {}) {
  if (!note) {
    toast.error('No note to export');
    return null;
  }

  const {
    filename = `${note.title || 'Untitled'}.pdf`,
    margin = 10,
    pageSize = 'a4',
    orientation = 'portrait',
  } = options;

  // Create a styled HTML container for the note
  const container = document.createElement('div');
  const rawHtml = `
    <style>
      .pdf-export {
        font-family: 'Inter', system-ui, sans-serif;
        color: #1a1a1a;
        line-height: 1.6;
        padding: 20px;
      }
      .pdf-export h1 {
        font-family: 'Inter', system-ui, sans-serif;
        font-size: 24px;
        font-weight: 700;
        margin-bottom: 16px;
        padding-bottom: 8px;
        border-bottom: 2px solid #e5e5e5;
      }
      .pdf-export .pdf-meta {
        font-size: 11px;
        color: #666;
        margin-bottom: 20px;
      }
      .pdf-export .pdf-content {
        font-size: 12px;
      }
      .pdf-export .pdf-content h1 { font-size: 20px; font-weight: 700; margin: 16px 0 8px; }
      .pdf-export .pdf-content h2 { font-size: 18px; font-weight: 600; margin: 14px 0 6px; }
      .pdf-export .pdf-content h3 { font-size: 16px; font-weight: 600; margin: 12px 0 4px; }
      .pdf-export .pdf-content p { margin: 8px 0; }
      .pdf-export .pdf-content ul, .pdf-export .pdf-content ol { margin: 8px 0; padding-left: 24px; }
      .pdf-export .pdf-content li { margin: 4px 0; }
      .pdf-export .pdf-content blockquote {
        border-left: 3px solid #ddd;
        padding-left: 12px;
        margin: 8px 0;
        color: #555;
        font-style: italic;
      }
      .pdf-export .pdf-content pre {
        background: #f5f5f5;
        padding: 12px;
        border-radius: 4px;
        font-family: 'JetBrains Mono', monospace;
        font-size: 11px;
        overflow-x: auto;
      }
      .pdf-export .pdf-content code {
        background: #f0f0f0;
        padding: 2px 4px;
        border-radius: 3px;
        font-family: 'JetBrains Mono', monospace;
        font-size: 11px;
      }
      .pdf-export .pdf-content table {
        border-collapse: collapse;
        width: 100%;
        margin: 12px 0;
      }
      .pdf-export .pdf-content th, .pdf-export .pdf-content td {
        border: 1px solid #ddd;
        padding: 8px;
        text-align: left;
      }
      .pdf-export .pdf-content th {
        background: #f5f5f5;
        font-weight: bold;
      }
      .pdf-export .pdf-content a {
        color: #0066cc;
        text-decoration: underline;
      }
      .pdf-export .pdf-content mark {
        background: #fff59d;
        padding: 1px 2px;
      }
      .pdf-export .pdf-content hr {
        border: none;
        border-top: 1px solid #ddd;
        margin: 16px 0;
      }
      .pdf-export .note-link {
        color: #0066cc;
        background: #e3f2fd;
        padding: 1px 4px;
        border-radius: 3px;
      }
      .pdf-export .pdf-footer {
        margin-top: 30px;
        padding-top: 10px;
        border-top: 1px solid #e5e5e5;
        font-size: 10px;
        color: #999;
        text-align: center;
      }
    </style>
    <div class="pdf-export">
      <h1>${escapeHtml(note.title || 'Untitled')}</h1>
      <div class="pdf-meta">
        Created: ${formatDate(note.created_at)} | 
        Last modified: ${formatDate(note.updated_at)}
        ${note.tags?.length > 0 ? ` | Tags: ${note.tags.join(', ')}` : ''}
      </div>
      <div class="pdf-content">
        ${convertMarkdownToHtml(note.content || '')}
      </div>
      <div class="pdf-footer">
        Exported from VaultNotes on ${new Date().toLocaleDateString()}
      </div>
    </div>
  `;
  container.innerHTML = DOMPurify.sanitize(rawHtml, { ADD_TAGS: ['style'], FORCE_BODY: true });

  const pdfOptions = {
    margin,
    filename,
    image: { type: 'jpeg', quality: 0.98 },
    html2canvas: { 
      scale: 2,
      useCORS: true,
      letterRendering: true,
    },
    jsPDF: { 
      unit: 'mm', 
      format: pageSize, 
      orientation 
    },
    pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
  };

  try {
    toast.loading('Generating PDF...', { id: 'pdf-export' });
    
    await html2pdf()
      .set(pdfOptions)
      .from(container)
      .save();

    toast.success('PDF exported successfully!', { id: 'pdf-export' });
    return true;
  } catch (error) {
    console.error('PDF export failed:', error);
    toast.error('Failed to export PDF', { id: 'pdf-export' });
    return false;
  }
}

/**
 * Convert basic markdown to HTML for PDF
 */
function convertMarkdownToHtml(markdown) {
  if (!markdown) return '';

  let html = markdown
    // Escape HTML first
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    
    // Headers
    .replace(/^### (.*$)/gm, '<h3>$1</h3>')
    .replace(/^## (.*$)/gm, '<h2>$1</h2>')
    .replace(/^# (.*$)/gm, '<h1>$1</h1>')
    
    // Bold and Italic
    .replace(/\*\*\*(.*?)\*\*\*/g, '<strong><em>$1</em></strong>')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/~~(.*?)~~/g, '<del>$1</del>')
    
    // Code blocks
    .replace(/```([\s\S]*?)```/g, '<pre><code>$1</code></pre>')
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    
    // Blockquotes
    .replace(/^> (.*$)/gm, '<blockquote>$1</blockquote>')
    
    // Horizontal rules
    .replace(/^---$/gm, '<hr>')
    .replace(/^\*\*\*$/gm, '<hr>')
    
    // Note links [[note name]]
    .replace(/\[\[([^\]]+)\]\]/g, '<span class="note-link">$1</span>')
    
    // Links
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>')
    
    // Task lists
    .replace(/^- \[x\] (.*$)/gm, '<li style="list-style: none;">☑ $1</li>')
    .replace(/^- \[ \] (.*$)/gm, '<li style="list-style: none;">☐ $1</li>')
    
    // Unordered lists
    .replace(/^- (.*$)/gm, '<li>$1</li>')
    .replace(/^\* (.*$)/gm, '<li>$1</li>')
    
    // Ordered lists
    .replace(/^\d+\. (.*$)/gm, '<li>$1</li>')
    
    // Paragraphs (double newlines)
    .replace(/\n\n/g, '</p><p>')
    
    // Single newlines to <br> within paragraphs
    .replace(/\n/g, '<br>');

  // Wrap in paragraph tags
  html = '<p>' + html + '</p>';
  
  // Clean up empty paragraphs
  html = html.replace(/<p><\/p>/g, '');
  
  // Wrap consecutive <li> elements in <ul>
  html = html.replace(/(<li>.*?<\/li>)+/gs, '<ul>$&</ul>');
  
  return html;
}

/**
 * Escape HTML special characters
 */
function escapeHtml(text) {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Format date for display
 */
function formatDate(dateStr) {
  if (!dateStr) return 'Unknown';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return 'Unknown';
  }
}

export default exportNoteToPDF;
